// The sync privacy transform. Everything that leaves the machine goes through
// toSyncTurn/toSyncEvent: cwd becomes a truncated hash + basename, and
// gitBranch/filePath are dropped entirely. The local DB already contains no
// message text, thinking content or tool inputs.
import { createHash } from "node:crypto";
import {
  basename,
  type SideEvent,
  type SyncEventWire,
  type SyncTurnWire,
  type TurnEvent,
} from "@tktuner/core";

/** sha256(cwd) truncated to 16 hex chars — stable id; the path never leaves. */
export function projectIdOf(cwd: string): string {
  return createHash("sha256").update(cwd).digest("hex").slice(0, 16);
}

export function projectNameOf(cwd: string): string {
  return cwd ? basename(cwd).slice(0, 120) : "unknown";
}

export function toSyncTurn(t: TurnEvent): SyncTurnWire {
  return {
    turnKey: t.messageId ?? t.uuid,
    uuid: t.uuid,
    messageId: t.messageId,
    requestId: t.requestId,
    sessionId: t.sessionId,
    agentId: t.agentId,
    parentSessionId: t.parentSessionId,
    isSidechain: t.isSidechain,
    projectId: projectIdOf(t.projectPath),
    projectName: projectNameOf(t.projectPath),
    slug: t.slug?.slice(0, 120),
    ts: t.timestamp,
    model: t.model.slice(0, 120),
    usage: t.usage,
    tools: t.tools.slice(0, 500).map((name) => name.slice(0, 100)),
    numToolUses: t.numToolUses,
    thinkingChars: t.thinkingChars,
    textChars: t.textChars,
    stopReason: t.stopReason?.slice(0, 60),
    version: t.cliVersion?.slice(0, 40),
    speed: t.speed?.slice(0, 20),
    inferenceGeo: t.inferenceGeo?.slice(0, 20),
  };
}

export function toSyncEvent(id: number, e: SideEvent): SyncEventWire {
  return {
    localEventId: id,
    sessionId: e.sessionId,
    ts: e.timestamp,
    kind: e.kind,
    data: e.data, // already whitelisted at parse time — never prompt/content
  };
}
